import { useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import LumetierWordmark from "@/components/LumetierWordmark";
import PasswordInput from "@/components/PasswordInput";

function strengthScore(pw) {
  let s = 0;
  if (!pw) return 0;
  if (pw.length >= 8) s += 1;
  if (pw.length >= 12) s += 1;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s += 1;
  if (/\d/.test(pw)) s += 1;
  if (/[^A-Za-z0-9]/.test(pw)) s += 1;
  return Math.min(s, 4);
}

const STRENGTH_LABELS = ["Too short", "Weak", "Okay", "Strong", "Excellent"];
const STRENGTH_COLORS = ["bg-rose-500", "bg-rose-400", "bg-amber-400", "bg-emerald-400", "bg-emerald-500"];

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const score = useMemo(() => strengthScore(pw1), [pw1]);

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (pw1.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (pw1 !== pw2) {
      setError("The two passwords don't match.");
      return;
    }
    if (!token) {
      setError("Reset token is missing. Click the link from your email again.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw1 });
      toast.success("Password updated. Sign in with your new password.");
      nav("/admin/login", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't reset password.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="min-h-screen relative overflow-hidden bg-[#070709] text-stone-100 flex items-center justify-center px-6 py-12"
      data-testid="reset-password-page"
    >
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 -left-40 w-[36rem] h-[36rem] rounded-full opacity-[0.18]" style={{ background: "radial-gradient(circle at center, hsl(var(--gold) / 0.55), transparent 60%)" }} />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-40 -right-40 w-[40rem] h-[40rem] rounded-full opacity-[0.14]" style={{ background: "radial-gradient(circle at center, hsl(var(--gold) / 0.45), transparent 60%)" }} />

      <div className="relative z-10 w-full max-w-md">
        <div className="flex flex-col items-center text-center mb-10">
          <LumetierWordmark tone="light" className="text-xl" testid="reset-wordmark" />
          <div className="mt-2 text-[10px] uppercase tracking-[0.32em] text-stone-500">
            Studio Manager
          </div>
        </div>

        <form onSubmit={submit} className="bg-white/[0.03] backdrop-blur-sm border border-white/[0.07] rounded-sm px-8 py-10 shadow-[0_30px_120px_-20px_rgba(0,0,0,0.6)]">
          <h1 className="font-serif text-3xl sm:text-4xl font-light leading-tight text-stone-100">
            Set a new password.
          </h1>
          <p className="mt-3 text-sm text-stone-400 leading-relaxed">
            Choose something you&apos;ll remember. At least 8 characters; mixing
            letters, numbers and a symbol makes it harder to guess.
          </p>

          <div className="mt-9 space-y-6">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.28em] text-stone-500 font-medium">
                New password
              </span>
              <PasswordInput
                required
                data-testid="reset-pw1-input"
                value={pw1}
                onChange={(e) => setPw1(e.target.value)}
                inputClassName="mt-2 w-full bg-transparent border-b border-white/[0.12] py-3 text-stone-100 placeholder-stone-600 outline-none transition-colors focus:border-[hsl(var(--gold))]"
                autoComplete="new-password"
                minLength={8}
              />
              {pw1 && (
                <div className="mt-3" data-testid="reset-strength">
                  <div className="flex gap-1">
                    {[0, 1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 ${i < score ? STRENGTH_COLORS[score] : "bg-white/[0.08]"}`}
                      />
                    ))}
                  </div>
                  <div className="mt-2 text-[10px] uppercase tracking-[0.28em] text-stone-500">
                    {STRENGTH_LABELS[score]}
                  </div>
                </div>
              )}
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.28em] text-stone-500 font-medium">
                Confirm new password
              </span>
              <PasswordInput
                required
                data-testid="reset-pw2-input"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                inputClassName="mt-2 w-full bg-transparent border-b border-white/[0.12] py-3 text-stone-100 placeholder-stone-600 outline-none transition-colors focus:border-[hsl(var(--gold))]"
                autoComplete="new-password"
                minLength={8}
              />
            </label>
          </div>

          {error && (
            <div className="mt-5 text-xs text-rose-400" data-testid="reset-error">{error}</div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="reset-submit-btn"
            className="mt-9 w-full inline-flex items-center justify-center h-12 bg-[hsl(var(--gold))] text-stone-900 text-xs uppercase tracking-[0.28em] font-semibold transition-all hover:bg-[hsl(var(--gold))]/90 hover:shadow-[0_8px_30px_-10px_hsl(var(--gold))] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? "Saving…" : "Update password"}
          </button>

          <Link
            to="/admin/login"
            className="mt-6 block text-center text-[10px] uppercase tracking-[0.28em] text-stone-500 hover:text-stone-200 transition-colors"
            data-testid="reset-back-link"
          >
            ← Back to sign in
          </Link>
        </form>
      </div>
    </div>
  );
}
